/**
 * Свой шрифт воронки (слово владельца 20.10.2026) — кабинетная половина.
 *
 * Автор загружает файл шрифта (сервер: `POST /projects/:id/uploads/fonts`,
 * тип по содержимому, то же хранилище, что картинки), семейство и его файлы
 * лежат в `settings.customFonts`, а выбор — строкой `custom:<семейство>` в том
 * же `fontId`, что и у встроенных шрифтов (у воронки и у блока).
 *
 * Здесь: разбор ссылки, сборка правил `@font-face` и хук, вешающий их на
 * страницу. Правила собираются только из проверенного — те же условия, что у
 * сервера (`backend/src/ai-funnels/block-schema/custom-fonts.ts`): имя
 * семейства из закрытого набора знаков, адрес https без кавычек, скобок,
 * пробелов и угловых скобок, жирность 100..900, начертание normal|italic,
 * формат из четырёх, поднабор символов (`unicodeRange`, WEB-3943) той же
 * формой, что у сервера (`normalizeUnicodeRange`). Непроверенное молча
 * пропускается: подставить своё правило CSS через имя, адрес или поднабор
 * нельзя, даже если сохранённые настройки подделаны в обход сервера.
 *
 * ПОДНАБОР (WEB-3943). Начертание может лежать несколькими файлами —
 * латиница и кириллица Google Fonts из разбора по ссылке; у каждого файла
 * своё правило `@font-face` с `unicode-range`, браузер качает кириллический
 * файл, только когда на экране есть кириллица. Файл без поднабора — прежняя
 * форма, правило байт в байт как раньше. Форма и потолки — общая таблица
 * случаев с сервером (`backend/src/ai-funnels/block-schema/custom-fonts.cases.json`).
 *
 * ПРАВКА СПИСКА В РЕДАКТОРЕ (`withCustomFontFile`, `withoutCustomFont`,
 * `isReservedCustomFontFamily`, `customFontFilesOk`) — в `customFontsEdit.ts`
 * (WEB-3943-2): этот модуль зовёт плеер покупателя (`useCustomFontFaces`) и
 * лежит в первом экране публичной воронки, а код редактора туда не нужен.
 */
import { useEffect } from 'react';
import { isCustomFontFamily } from './customFontRef';

export {
  CUSTOM_FONT_PREFIX,
  customFontFamilyOf,
  customFontRef,
  isCustomFontFamily,
} from './customFontRef';

export type CustomFontFormat = 'woff2' | 'woff' | 'truetype' | 'opentype';
export const CUSTOM_FONT_FORMATS: readonly CustomFontFormat[] = [
  'woff2',
  'woff',
  'truetype',
  'opentype',
];

export interface CustomFontFile {
  url: string;
  weight: number;
  style: 'normal' | 'italic';
  format: CustomFontFormat;
  /** Поднабор символов в форме CSS `unicode-range`, приведённый; нет — весь набор. */
  unicodeRange?: string;
}

export interface CustomFont {
  family: string;
  files: CustomFontFile[];
}

/**
 * Те же пределы, что у сервера (MAX_CUSTOM_FONTS, MAX_CUSTOM_FONT_FACES,
 * MAX_CUSTOM_FONT_FILES, UNICODE_RANGE_MAX_*).
 */
export const MAX_CUSTOM_FONTS = 4;
/** Начертаний (пар «жирность / начертание») у семейства. */
export const MAX_CUSTOM_FONT_FACES = 8;
/** Файлов у семейства (по два поднабора на начертание). */
export const MAX_CUSTOM_FONT_FILES = 16;
export const UNICODE_RANGE_MAX_LENGTH = 400;
export const UNICODE_RANGE_MAX_RANGES = 32;

/**
 * Один диапазон: `U+<hex>` или `U+<hex>-<hex>` (1–6 знаков) — группы 1 и 2,
 * либо маска `U+<hex>?…` до 6 знаков вместе с `?` — группа 3. Одно выражение
 * вместо разбора по веткам: код едет в первый экран плеера (WEB-3943, вес).
 */
const RANGE_ITEM_RE =
  /^U\+(?:([0-9A-F]{1,6})(?:-([0-9A-F]{1,6}))?|(?=[0-9A-F?]{1,6}$)([0-9A-F]*\?+))$/;

/**
 * Поднабор символов в форме CSS `unicode-range`, приведённый (заглавные, без
 * пробелов), либо null — форма неверна. Зеркало серверного
 * `normalizeUnicodeRange` (custom-fonts.ts), случаи — общая таблица. Начало
 * маски — `?` как 0; начало ≤ конца ≤ 10FFFF.
 */
export function normalizeUnicodeRange(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw || raw.length > UNICODE_RANGE_MAX_LENGTH)
    return null;
  const items = raw.split(',').map((x) => x.trim().toUpperCase());
  return items.length <= UNICODE_RANGE_MAX_RANGES &&
    items.every((item) => {
      const m = RANGE_ITEM_RE.exec(item);
      if (!m) return false;
      const start = parseInt((m[1] ?? m[3]).replace(/\?/g, '0'), 16);
      const end = m[2] ? parseInt(m[2], 16) : start;
      return start <= end && end <= 0x10ffff;
    })
    ? items.join(',')
    : null;
}

const URL_RE = /^https:\/\/[^\s"'()\\<>]+$/;
const URL_MAX = 500;

function readFile(raw: unknown): CustomFontFile | null {
  if (!raw || typeof raw !== 'object') return null;
  const f = raw as Record<string, unknown>;
  if (
    typeof f.url !== 'string' ||
    f.url.length > URL_MAX ||
    !URL_RE.test(f.url)
  )
    return null;
  if (
    typeof f.weight !== 'number' ||
    !Number.isInteger(f.weight) ||
    f.weight < 100 ||
    f.weight > 900
  )
    return null;
  if (f.style !== 'normal' && f.style !== 'italic') return null;
  if (!CUSTOM_FONT_FORMATS.includes(f.format as CustomFontFormat)) return null;
  const unicodeRange =
    f.unicodeRange === undefined
      ? undefined
      : normalizeUnicodeRange(f.unicodeRange);
  if (unicodeRange === null) return null;
  return {
    url: f.url,
    weight: f.weight,
    style: f.style,
    format: f.format as CustomFontFormat,
    ...(unicodeRange && { unicodeRange }),
  };
}

/** Верные семейства и их верные файлы из непроверенного `settings.customFonts`. */
export function readCustomFonts(raw: unknown): CustomFont[] {
  if (!Array.isArray(raw)) return [];
  const out: CustomFont[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const font = item as Record<string, unknown>;
    if (!isCustomFontFamily(font.family) || !Array.isArray(font.files))
      continue;
    const files = font.files
      .map(readFile)
      .filter((f): f is CustomFontFile => f !== null);
    if (files.length > 0) out.push({ family: font.family, files });
  }
  return out;
}

/**
 * Одно правило `@font-face` на файл (у файла с поднабором — с
 * `unicode-range`); пустая строка — нечего подключать.
 */
export function buildCustomFontFaceCss(raw: unknown): string {
  return readCustomFonts(raw)
    .flatMap((font) =>
      font.files.map(
        (file) =>
          `@font-face{font-family:"${font.family}";src:url("${file.url}") format("${file.format}");` +
          `font-weight:${file.weight};font-style:${file.style};font-display:swap;` +
          (file.unicodeRange ? `unicode-range:${file.unicodeRange};` : '') +
          '}',
      ),
    )
    .join('\n');
}

/**
 * Вешает правила `@font-face` своих шрифтов воронки на страницу (элемент
 * `<style data-custom-font-faces>` в head) и убирает их при размонтировании
 * или смене списка. Зовётся везде, где зовётся `ensureFontsLoaded`: публичная
 * воронка, пейволл, предпросмотр в редакторе (сторож —
 * `customFonts.callers.test.ts`).
 */
export function useCustomFontFaces(customFonts: unknown): void {
  const css = buildCustomFontFaceCss(customFonts);
  useEffect(() => {
    if (!css || typeof document === 'undefined') return undefined;
    const el = document.createElement('style');
    el.setAttribute('data-custom-font-faces', '');
    el.textContent = css;
    document.head.appendChild(el);
    return () => {
      el.remove();
    };
  }, [css]);
}
